// Add / edit one red flag warning message: the mail an employee gets when they reach a
// given red-flag number.
//
// The admin writes only the subject and the message. The system adds the facts itself —
// which red flag, why, and the employee's red flags in the last 12 months — so the text
// here cannot get them wrong; the note under the editor says so. Details that differ per
// employee (their name, the number they have reached) go in as placeholders, inserted
// from the chips rather than typed, because the server refuses a placeholder it does not
// know.
//
// A new message starts from suggested wording for its number, so nobody faces an empty
// box. Changing the number swaps the suggestion only while the text is still the
// suggestion: once the admin has edited it, their text stays.
//
// Preview and "Send test to" use the text as typed, saved or not, with sample data. They
// sit outside the <form> so pressing Enter in the test address never saves the message.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Loader2 } from 'lucide-react';
import Modal from '../Modal';
import ModalFormFooterActions from '../ModalFormFooterActions';
import { ModalFormErrorBanner } from '../ModalFormErrorBanner';
import {
  redFlagWarningMailsAPI,
  type RedFlagWarningConfig,
  type RedFlagWarningDraft,
  type RedFlagWarningStep,
} from '../../services/api';
import { SAMPLE_NOTE, apiMessage, flagNumberLabel } from './warningMailText';
import { WarningPreviewFrame, WarningTestSend, type WarningPreviewData } from './WarningPreview';

const FORM_ID = 'rfw-message-form';
const SUBJECT_MAX = 200;
const BODY_MAX = 5000;
/** The number picker always offers at least this many; more when the Start Exit level is higher. */
const MIN_NUMBERS = 20;

interface Suggested {
  subjectTemplate: string;
  bodyTemplate: string;
  andLater: boolean;
}

type Field = 'subject' | 'body';

export function WarningMessageModal({
  projectId, config, step, onClose, onSaved,
}: {
  projectId: number;
  config: RedFlagWarningConfig;
  /** null = a new message. */
  step: RedFlagWarningStep | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const editing = step !== null;
  const exitLevel = config.startExit.minRedFlags;

  const usedNumbers = useMemo(
    () => new Set(config.steps.filter((s) => s.id !== step?.id).map((s) => s.flagNumber)),
    [config.steps, step],
  );
  const numbers = useMemo(() => {
    const top = Math.max(MIN_NUMBERS, exitLevel, step?.flagNumber ?? 0);
    return Array.from({ length: top }, (_, i) => i + 1);
  }, [exitLevel, step]);

  const [flagNumber, setFlagNumber] = useState<number | null>(
    () => (step ? step.flagNumber : numbers.find((n) => !usedNumbers.has(n)) ?? null),
  );
  const [subject, setSubject] = useState(step?.subjectTemplate ?? '');
  const [body, setBody] = useState(step?.bodyTemplate ?? '');
  const [andLater, setAndLater] = useState(step?.andLater ?? false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Suggested wording (new messages only) ──────────────────────────────────
  const [suggested, setSuggested] = useState<Suggested | null>(null);
  const [suggestedNote, setSuggestedNote] = useState<string | null>(null);
  /** The suggestion currently sitting in the form untouched, if any. */
  const inForm = useRef<Suggested | null>(null);
  const andLaterTouched = useRef(false);
  const latest = useRef({ subject, body });
  latest.current = { subject, body };

  useEffect(() => {
    if (editing || flagNumber === null) return undefined;
    let alive = true;
    setSuggested(null);
    setSuggestedNote(null);
    redFlagWarningMailsAPI
      .starter(projectId, flagNumber)
      .then((res) => {
        if (!alive) return;
        const next = res.data.data;
        setSuggested(next);
        const untouched =
          latest.current.subject === (inForm.current?.subjectTemplate ?? '')
          && latest.current.body === (inForm.current?.bodyTemplate ?? '');
        if (untouched) {
          setSubject(next.subjectTemplate);
          setBody(next.bodyTemplate);
          inForm.current = next;
        }
        if (!andLaterTouched.current) setAndLater(next.andLater);
      })
      .catch(() => {
        if (!alive) return;
        if (!andLaterTouched.current) setAndLater(flagNumber >= exitLevel);
        setSuggestedNote('Could not load the suggested text. Write your own below.');
      });
    return () => {
      alive = false;
    };
  }, [editing, flagNumber, projectId, exitLevel]);

  const differsFromSuggested =
    suggested !== null && (subject !== suggested.subjectTemplate || body !== suggested.bodyTemplate);

  const applySuggested = () => {
    if (!suggested) return;
    setSubject(suggested.subjectTemplate);
    setBody(suggested.bodyTemplate);
    inForm.current = suggested;
  };

  // ── Placeholder chips: insert where the cursor last was ────────────────────
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastField = useRef<Field>('body');

  const insertPlaceholder = (token: string) => {
    const field = lastField.current;
    const el = field === 'subject' ? subjectRef.current : bodyRef.current;
    const value = field === 'subject' ? subject : body;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    const text = `{${token}}`;
    const next = `${value.slice(0, start)}${text}${value.slice(end)}`;
    if (field === 'subject') setSubject(next);
    else setBody(next);
    // Put the cursor after the inserted placeholder, or the next character typed lands inside it.
    const pos = start + text.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(pos, pos);
    });
  };

  // ── Preview / test send work on the text as typed ──────────────────────────
  const draft: RedFlagWarningDraft | null =
    flagNumber === null ? null : { flagNumber, subjectTemplate: subject, bodyTemplate: body };
  const draftKey = JSON.stringify(draft);

  const [preview, setPreview] = useState<WarningPreviewData | null>(null);
  const [previewOf, setPreviewOf] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const runPreview = async () => {
    if (!draft) {
      setPreviewError('Choose a red flag number first.');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await redFlagWarningMailsAPI.preview(projectId, draft);
      setPreview({ subject: res.data.data.subject, html: res.data.data.html });
      setPreviewOf(draftKey);
    } catch (e) {
      setPreview(null);
      setPreviewOf(null);
      setPreviewError(apiMessage(e, 'Could not build the preview. Please try again.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (flagNumber === null) {
      setFormError('Choose a red flag number.');
      return;
    }
    if (!subject.trim()) {
      setFormError('Write a subject.');
      return;
    }
    if (!body.trim()) {
      setFormError('Write the message.');
      return;
    }
    if (body.length > BODY_MAX) {
      setFormError(`The message is ${body.length.toLocaleString()} characters long. Keep it to ${BODY_MAX.toLocaleString()}.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        flagNumber,
        subjectTemplate: subject.trim(),
        bodyTemplate: body,
        andLater,
        isActive: step ? step.isActive : true,
      };
      if (step) await redFlagWarningMailsAPI.updateStep(projectId, step.id, payload);
      else await redFlagWarningMailsAPI.createStep(projectId, payload);
      await onSaved();
    } catch (err) {
      setFormError(apiMessage(err, 'Could not save the message. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const namesTheNumber = /\{flag_number\}|\{flag_count\}/.test(`${subject}\n${body}`);

  return (
    <Modal
      isOpen
      onClose={submitting ? () => undefined : onClose}
      title={editing ? 'Edit message' : 'Add message'}
      size="xl"
      footer={
        <ModalFormFooterActions
          onCancel={onClose}
          submitting={submitting}
          editing={editing}
          createLabel="Add message"
          formId={FORM_ID}
        />
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <form id={FORM_ID} onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="rfw-number" className="mb-1 block text-sm font-medium text-gray-700">Red flag number</label>
            <select
              id="rfw-number"
              value={flagNumber ?? ''}
              onChange={(e) => setFlagNumber(e.target.value ? Number(e.target.value) : null)}
              className="form-input"
            >
              {flagNumber === null && <option value="">Choose…</option>}
              {numbers.map((n) => (
                <option key={n} value={n} disabled={usedNumbers.has(n)}>
                  {flagNumberLabel(n)}{usedNumbers.has(n) ? ' — already has a message' : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Sent when an employee reaches this many red flags in the last 12 months. Exit Reviews lists a person at{' '}
              {exitLevel} or more.
            </p>
            {suggestedNote && <p className="mt-1 text-xs text-amber-700">{suggestedNote}</p>}
            {!editing && differsFromSuggested && flagNumber !== null && (
              <button type="button" onClick={applySuggested} className="mt-1 text-xs font-medium text-brand hover:underline">
                Use the suggested text for the {flagNumberLabel(flagNumber)}
              </button>
            )}
          </div>

          <div>
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={andLater}
                onChange={(e) => { andLaterTouched.current = true; setAndLater(e.target.checked); }}
              />
              Also send this message for later red flags that have no message of their own
            </label>
            {andLater && !namesTheNumber && (
              <p className="mt-1 pl-6 text-xs text-amber-700">
                This message is also used for later red flags — use {'{flag_number}'} instead of typing the number.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="rfw-subject" className="mb-1 block text-sm font-medium text-gray-700">Subject</label>
            <input
              id="rfw-subject"
              ref={subjectRef}
              type="text"
              value={subject}
              maxLength={SUBJECT_MAX}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => { lastField.current = 'subject'; }}
              className="form-input"
            />
          </div>

          <div>
            <label htmlFor="rfw-body" className="mb-1 block text-sm font-medium text-gray-700">Body (Markdown)</label>
            <textarea
              id="rfw-body"
              ref={bodyRef}
              rows={12}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => { lastField.current = 'body'; }}
              className="form-input font-mono text-xs"
            />
            <p className={`mt-1 text-xs ${body.length > BODY_MAX ? 'text-red-700' : 'text-gray-500'}`}>
              {body.length.toLocaleString()} of {BODY_MAX.toLocaleString()} characters
            </p>
          </div>

          <div>
            <div className="mb-1 text-sm font-medium text-gray-700">Add a detail</div>
            <div className="flex flex-wrap gap-1.5">
              {config.placeholders.map((p) => (
                <button
                  key={p.token}
                  type="button"
                  onClick={() => insertPlaceholder(p.token)}
                  title={`Adds {${p.token}} — for example: ${p.sample}`}
                  className="rounded-full border border-[#A9BFDD] bg-[#EDF2F8] px-2 py-0.5 text-xs text-[#1B3A54] hover:bg-[#dfe8f3]"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Click one to add it where your cursor is, in the subject or the body. It is filled in for each employee
              when the mail is sent.
            </p>
          </div>

          <p className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            The system adds the red flag details and the employee’s red flags in the last 12 months below your message.
          </p>

          {formError && <ModalFormErrorBanner message={formError} />}
        </form>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-sm font-medium text-gray-700">How the mail will look</h4>
              <button
                type="button"
                onClick={() => void runPreview()}
                disabled={previewLoading}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {previewLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  : <Eye className="h-4 w-4" aria-hidden />}
                Preview
              </button>
            </div>
            {previewError && <ModalFormErrorBanner message={previewError} />}
            {preview ? (
              <>
                <WarningPreviewFrame preview={preview} />
                <p className="text-xs text-gray-500">
                  {previewOf === draftKey
                    ? SAMPLE_NOTE
                    : 'You have changed the text since this preview. Press Preview to see it again.'}
                </p>
              </>
            ) : !previewError && (
              <p className="rounded-lg border border-dashed border-gray-300 px-3 py-8 text-center text-sm text-gray-500">
                Press Preview to see this message as an employee would, filled with sample data.
              </p>
            )}
          </div>

          <WarningTestSend projectId={projectId} draft={draft} />
        </div>
      </div>
    </Modal>
  );
}

export default WarningMessageModal;
